import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  Download,
  Clock,
  Eye,
  Loader2,
} from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { leaveApi, employeeApi, departmentApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import { format } from 'date-fns';
import { cn, formatDate as formatDateUtil } from '../../lib/utils';

type ViewMode = 'month' | 'week' | 'year';

interface LeaveCalendarEvent {
  id: string;
  type: 'leave' | 'holiday';
  date: Date;
  employee?: any;
  leave?: any;
  holiday?: any;
  status?: string;
}

interface NewLeaveFormData {
  leave_policy_id: string;
  startDate: Date;
  endDate: Date;
  reason: string;
  duration: number;
}

const LeaveCalendar: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<LeaveCalendarEvent | null>(null);
  const [isEventDetailOpen, setIsEventDetailOpen] = useState(false);
  const [isNewLeaveOpen, setIsNewLeaveOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [newLeaveForm, setNewLeaveForm] = useState<NewLeaveFormData>({
    leave_policy_id: '',
    startDate: new Date(),
    endDate: new Date(),
    reason: '',
    duration: 1
  });

  // Month and day names - defined before hooks that use them
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get current user's employee ID from auth context
  const currentUserId = user?.id || '';
  const currentUserName = user ? `${user.first_name} ${user.last_name}` : '';
  const { isUserView, canViewLeaveCalendarAll } = useManagerViewPermission();

  // Fetch leave requests
  const { data: leaveRequests = [], isLoading: requestsLoading } = useQuery<any[]>({
    queryKey: ['leaveRequests'],
    queryFn: async () => {
      const result = await leaveApi.requests.getAll();
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch holidays for current month/year
  const { data: holidays = [] } = useQuery<any[]>({
    queryKey: ['holidays', currentDate.getFullYear()],
    queryFn: async () => {
      const result = await leaveApi.holidays.getAll({ year: currentDate.getFullYear() });
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch employees
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['employees'],
    queryFn: async () => {
      const result = await employeeApi.getAll({});
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch departments
  const { data: departments = [] } = useQuery<any[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const result = await departmentApi.getAll();
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch policies
  const { data: policies = [] } = useQuery<any[]>({
    queryKey: ['leavePolicies'],
    queryFn: async () => {
      const result = await leaveApi.policies.getAll();
      return Array.isArray(result) ? result : [];
    },
  });

  // Create leave request mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.requests.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      setIsNewLeaveOpen(false);
      toast({ title: 'Leave request submitted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Helper functions
  const getFullName = (firstName: string, lastName?: string): string => {
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  const getInitials = (firstName: string, lastName?: string): string => {
    if (!lastName) {
      return firstName.charAt(0).toUpperCase();
    }
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const formatDate = (date: Date | string): string => {
    return formatDateUtil(date, { shortFormat: true });
  };

  const isSameDay = (date1: Date, date2: Date | string): boolean => {
    const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
    return date1.toDateString() === d2.toDateString();
  };

  const isWeekend = (date: Date): boolean => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const calculateDuration = (startDate: Date, endDate: Date): number => {
    let count = 0;
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      if (!isWeekend(currentDate)) {
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return count;
  };

  // New Leave Request handlers
  const openNewLeaveForm = (startDate?: Date, endDate?: Date) => {
    const defaultStart = startDate || new Date();
    const defaultEnd = endDate || startDate || new Date();

    setNewLeaveForm({
      leave_policy_id: policies.length > 0 ? policies[0].id : '',
      startDate: defaultStart,
      endDate: defaultEnd,
      reason: '',
      duration: calculateDuration(defaultStart, defaultEnd)
    });
    setIsNewLeaveOpen(true);
  };

  const handleFormSubmit = () => {
    if (!newLeaveForm.leave_policy_id || !newLeaveForm.reason.trim()) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    createMutation.mutate({
      employee_id: currentUserId,
      employee_name: currentUserName,
      leave_policy_id: newLeaveForm.leave_policy_id,
      start_date: newLeaveForm.startDate.toISOString().split('T')[0],
      end_date: newLeaveForm.endDate.toISOString().split('T')[0],
      days_count: newLeaveForm.duration,
      reason: newLeaveForm.reason,
      status: 'PENDING',
    });
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7));
    } else if (viewMode === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth()));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const goToNextMonth = () => {
    if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7));
    } else if (viewMode === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth()));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  };

  // Get header text based on view mode
  const getHeaderText = () => {
    if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
    } else if (viewMode === 'year') {
      return `${currentDate.getFullYear()}`;
    }
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  // Export calendar data
  const handleExport = () => {
    const csvRows = [];
    csvRows.push(['Date', 'Type', 'Employee', 'Status', 'Details'].join(','));

    calendarEvents.forEach(event => {
      const date = formatDate(event.date);
      const type = event.type === 'holiday' ? 'Holiday' : 'Leave';
      const employee = event.employee ? getFullName(event.employee.first_name, event.employee.last_name) : '-';
      const status = event.status || '-';
      const details = event.type === 'holiday' ? event.holiday?.name : event.leave?.reason || '';

      csvRows.push([date, type, employee, status, `"${details}"`].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `leave-calendar-${currentDate.getFullYear()}-${currentDate.getMonth() + 1}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    toast({ title: 'Calendar exported successfully' });
  };

  // Filter employees by department
  const filteredEmployees = useMemo(() => {
    if (selectedDepartment === 'all') return employees;
    return employees.filter((emp: any) => emp.department_id === selectedDepartment);
  }, [selectedDepartment, employees]);

  // Generate calendar events
  const calendarEvents = useMemo(() => {
    const events: LeaveCalendarEvent[] = [];

    // Add leave requests
    leaveRequests.forEach((leave: any) => {
      if ((isUserView || !canViewLeaveCalendarAll) && leave.employee_id !== currentUserId) {
        return;
      }

      // Try to find employee by ID first, then by name match
      let employee = employees.find((e: any) => e.id === leave.employee_id);

      // If not found by ID, try matching by name
      if (!employee && leave.employee_name) {
        const nameParts = leave.employee_name.trim().toLowerCase().split(' ');
        employee = employees.find((e: any) => {
          const empName = `${e.first_name || ''} ${e.last_name || ''}`.trim().toLowerCase();
          return empName === leave.employee_name.trim().toLowerCase() ||
            (nameParts[0] && e.first_name?.toLowerCase() === nameParts[0]);
        });
      }

      // Create a fallback employee object if not found
      const employeeData = employee || {
        id: leave.employee_id,
        first_name: leave.employee_name?.split(' ')[0] || 'Unknown',
        last_name: leave.employee_name?.split(' ').slice(1).join(' ') || '',
        department_id: leave.department_id || null
      };

      // Get department_id from leave request first, then employee
      const deptId = leave.department_id || employeeData.department_id;

      // Filter by department
      if (selectedDepartment !== 'all') {
        if (!deptId || deptId !== selectedDepartment) return;
      }

      // Add event for each day of the leave
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        // Skip weekends
        if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
          events.push({
            id: `${leave.id}-${currentDate.toISOString()}`,
            type: 'leave',
            date: new Date(currentDate),
            employee: employeeData,
            leave,
            status: leave.status
          });
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    // Add public holidays
    holidays.forEach((holiday: any) => {
      events.push({
        id: holiday.id,
        type: 'holiday',
        date: new Date(holiday.holiday_date),
        holiday
      });
    });

    return events;
  }, [selectedDepartment, leaveRequests, holidays, employees]);

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days: any[] = [];

    if (viewMode === 'week') {
      // Week view: Show 7 days starting from the current week's Sunday
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Start from Sunday

      const current = new Date(startOfWeek);
      for (let i = 0; i < 7; i++) {
        const dayEvents = calendarEvents.filter(event =>
          isSameDay(event.date, current)
        );

        days.push({
          date: new Date(current),
          isCurrentMonth: current.getMonth() === month,
          isToday: current.toDateString() === new Date().toDateString(),
          isWeekend: current.getDay() === 0 || current.getDay() === 6,
          events: dayEvents
        });

        current.setDate(current.getDate() + 1);
      }
    } else if (viewMode === 'year') {
      // Year view: Show all months with event counts
      for (let m = 0; m < 12; m++) {
        const monthEvents = calendarEvents.filter(event => {
          const eventDate = new Date(event.date);
          return eventDate.getFullYear() === year && eventDate.getMonth() === m;
        });

        days.push({
          month: m,
          year: year,
          monthName: monthNames[m],
          eventCount: monthEvents.length,
          leaveCount: monthEvents.filter(e => e.type === 'leave').length,
          holidayCount: monthEvents.filter(e => e.type === 'holiday').length,
        });
      }
    } else {
      // Month view (default): Show 42 days (6 weeks)
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

      const current = new Date(startDate);

      while (days.length < 42) { // 6 weeks
        const dayEvents = calendarEvents.filter(event =>
          isSameDay(event.date, current)
        );

        days.push({
          date: new Date(current),
          isCurrentMonth: current.getMonth() === month,
          isToday: current.toDateString() === new Date().toDateString(),
          isWeekend: current.getDay() === 0 || current.getDay() === 6,
          events: dayEvents
        });

        current.setDate(current.getDate() + 1);
      }
    }

    return days;
  }, [currentDate, calendarEvents, viewMode]);

  // Handle event click
  const handleEventClick = (event: LeaveCalendarEvent) => {
    setSelectedEvent(event);
    setIsEventDetailOpen(true);
  };

  // Get color class for leave status
  const getLeaveColorClass = (status: string): string => {
    switch (status) {
      case 'APPROVED':
        return 'bg-blue-500 text-white';
      case 'PENDING':
        return 'bg-yellow-400 text-black';
      case 'REJECTED':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      PENDING: 'secondary',
      APPROVED: 'default',
      REJECTED: 'destructive',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };



  if (requestsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading calendar...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-500">
      

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">View team availability and manage leave requests</h2>
          
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            <span>Export</span>
          </Button>
          <Button
            onClick={() => openNewLeaveForm()}
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>Add Leave</span>
          </Button>
        </div>
      </div>

      {/* Filters & Legend */}
      <div className="flex flex-col xl:flex-row gap-6 items-start xl:items-end justify-between bg-white/50 dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 backdrop-blur-sm">
        <div className="flex flex-col md:flex-row gap-4 items-end w-full xl:w-auto">
          <div className="w-full md:w-48 space-y-1.5">
            <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold ml-1">View Mode</Label>
            <Select value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
              <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 font-bold text-sm shadow-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-100 dark:border-slate-800 shadow-xl">
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(!isUserView && canViewLeaveCalendarAll) && (
            <div className="w-full md:w-64 space-y-1.5">
              <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold ml-1">Team / Department</Label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 font-bold text-sm shadow-sm">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-100 dark:border-slate-800 shadow-xl">
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept: any) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 bg-white dark:bg-slate-900 p-3.5 px-6 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm w-full xl:w-auto mt-4 xl:mt-0">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full shadow-sm"></div>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Holidays</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full shadow-sm"></div>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Approved</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-amber-400 rounded-full shadow-sm"></div>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Pending</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-slate-300 dark:bg-slate-700 rounded-full shadow-sm"></div>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Weekends</span>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
        <h2 className="text-2xl font-black text-[#143A62] dark:text-slate-100 flex items-center gap-2 tracking-tight">
          <CalendarIcon className="h-6 w-6 text-blue-500" />
          {getHeaderText()}
        </h2>
        <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 p-1.5 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
          <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </Button>
          <Button variant="outline" onClick={goToToday}>
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-lg bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardContent className="p-0">
          {viewMode === 'year' ? (
            /* Year View */
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 p-4">
              {calendarDays.map((monthData: any, index: number) => (
                <div
                  key={index}
                  className={cn(
                    "p-4 border rounded-lg cursor-pointer transition-colors",
                    "hover:bg-accent",
                    monthData.month === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear()
                      ? 'border-primary bg-accent'
                      : 'border-border bg-card'
                  )}
                  onClick={() => {
                    setCurrentDate(new Date(monthData.year, monthData.month, 1));
                    setViewMode('month');
                  }}
                >
                  <div className="font-medium text-foreground">{monthData.monthName}</div>
                  <div className="mt-2 space-y-1">
                    {monthData.leaveCount > 0 && (
                      <div className="text-xs text-white">{monthData.leaveCount} leaves</div>
                    )}
                    {monthData.holidayCount > 0 && (
                      <div className="text-xs text-white">{monthData.holidayCount} holidays</div>
                    )}
                    {monthData.eventCount === 0 && (
                      <div className="text-xs text-white opacity-70">No events</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : viewMode === 'week' ? (
            /* Week View */
            <>
              <div className="grid grid-cols-7 border-b border-gray-200">
                {dayNames.map(day => (
                  <div key={day} className="p-3 text-center font-semibold text-gray-700 bg-gray-50">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calendarDays.map((day: any, index: number) => (
                  <div
                    key={index}
                    className={`min-h-[200px] border-r border-b border-gray-200 p-2 relative ${day.isWeekend ? 'bg-gray-100' : ''
                      } ${day.isToday ? 'bg-blue-50 border-blue-300' : ''}`}
                    onClick={() => {
                      if (day.events.length === 0 && !day.isWeekend) {
                        openNewLeaveForm(day.date, day.date);
                      }
                    }}
                  >
                    <div className="flex flex-col h-full">
                      <div className={`text-sm font-medium mb-2 ${day.isToday ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                        {day.date.getDate()} {monthNames[day.date.getMonth()].slice(0, 3)}
                      </div>

                      <div className="flex-1 space-y-1 overflow-auto">
                        {day.events.filter((e: any) => e.type === 'holiday').map((event: any) => (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                            className="bg-green-500 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-green-600"
                          >
                            {event.holiday?.name}
                          </div>
                        ))}
                        {day.events.filter((e: any) => e.type === 'leave').map((event: any) => (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                            className={`text-xs px-2 py-1 rounded cursor-pointer flex items-center space-x-1 ${getLeaveColorClass(event.status!)}`}
                          >
                            {event.status === 'PENDING' && <Clock className="h-3 w-3 flex-shrink-0" />}
                            <span className="truncate">
                              {event.employee ? getFullName(event.employee.first_name, event.employee.last_name) : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* Month View (default) */
            <>
              <div className="grid grid-cols-7 border-b border-gray-200">
                {dayNames.map(day => (
                  <div key={day} className="p-3 text-center font-semibold text-gray-700 bg-gray-50">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calendarDays.map((day: any, index: number) => (
                  <div
                    key={index}
                    className={`min-h-[120px] border-r border-b border-gray-200 p-2 relative ${!day.isCurrentMonth ? 'bg-gray-50' : ''
                      } ${day.isWeekend ? 'bg-gray-100' : ''} ${day.isToday ? 'bg-blue-50 border-blue-300' : ''
                      }`}
                    onClick={() => {
                      if (day.events.length === 0 && day.isCurrentMonth && !day.isWeekend) {
                        openNewLeaveForm(day.date, day.date);
                      }
                    }}
                  >
                    <div className="flex flex-col h-full">
                      <div className={`text-sm font-medium mb-2 ${!day.isCurrentMonth ? 'text-gray-400' :
                          day.isToday ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                        {day.date.getDate()}
                      </div>

                      <div className="flex-1 space-y-1 overflow-hidden">
                        {day.events.filter((e: any) => e.type === 'holiday').map((event: any) => (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                            className="bg-green-500 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-green-600 truncate"
                          >
                            {event.holiday?.name}
                          </div>
                        ))}
                        {day.events.filter((e: any) => e.type === 'leave').slice(0, 3).map((event: any) => (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                            className={`text-xs px-2 py-1 rounded cursor-pointer truncate flex items-center space-x-1 ${getLeaveColorClass(event.status!)}`}
                          >
                            {event.status === 'PENDING' && <Clock className="h-3 w-3 flex-shrink-0" />}
                            <span className="truncate">
                              {event.employee ? getInitials(event.employee.first_name, event.employee.last_name) : ''}
                            </span>
                          </div>
                        ))}
                        {day.events.filter((e: any) => e.type === 'leave').length > 3 && (
                          <div className="text-xs text-gray-500 px-2">
                            +{day.events.filter((e: any) => e.type === 'leave').length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* New Leave Request Modal */}
      <Dialog open={isNewLeaveOpen} onOpenChange={setIsNewLeaveOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-0 border-none overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-slate-50 dark:border-slate-800">
            <DialogTitle className="flex items-center space-x-2 text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
              <Plus className="h-5 w-5 text-blue-500" />
              <span>New Leave Request</span>
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-slate-500">
              Submit a new leave request for approval
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-bold text-slate-400 tracking-widest ml-1" htmlFor="leave-type">Leave Type *</Label>
              <Select
                value={newLeaveForm.leave_policy_id}
                onValueChange={(value) => setNewLeaveForm(prev => ({ ...prev, leave_policy_id: value }))}
              >
                <SelectTrigger className="h-11 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 font-bold text-sm shadow-sm focus:ring-blue-500/20">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                  {policies.map((policy: any) => (
                    <SelectItem key={policy.id} value={policy.id} className="font-medium">
                      {policy.name} ({policy.days_per_year} days/year)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-bold text-slate-400 tracking-widest ml-1" htmlFor="start-date">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-bold rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 shadow-sm",
                        !newLeaveForm.startDate && "text-slate-400"
                      )}
                      data-testid="leave-start-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newLeaveForm.startDate ? format(newLeaveForm.startDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={newLeaveForm.startDate}
                      onSelect={(date) => {
                        if (date) {
                          const newEndDate = date > newLeaveForm.endDate ? date : newLeaveForm.endDate;
                          setNewLeaveForm(prev => ({
                            ...prev,
                            startDate: date,
                            endDate: newEndDate,
                            duration: calculateDuration(date, newEndDate)
                          }));
                        }
                      }}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px] font-bold text-slate-400 tracking-widest ml-1" htmlFor="end-date">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-bold rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 shadow-sm",
                        !newLeaveForm.endDate && "text-slate-400"
                      )}
                      data-testid="leave-end-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newLeaveForm.endDate ? format(newLeaveForm.endDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={newLeaveForm.endDate}
                      onSelect={(date) => {
                        if (date) {
                          setNewLeaveForm(prev => ({
                            ...prev,
                            endDate: date,
                            duration: calculateDuration(prev.startDate, date)
                          }));
                        }
                      }}
                      disabled={(date) => date < newLeaveForm.startDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-1.5 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
              <Label className="text-[13px] font-bold text-blue-400 tracking-widest">Duration</Label>
              <div className="flex items-center space-x-2 text-sm">
                <CalendarIcon className="h-4 w-4 text-blue-500" />
                <span className="font-bold text-blue-700 dark:text-blue-300">{newLeaveForm.duration} working days</span>
                <span className="text-blue-500 font-medium text-xs">
                  ({formatDate(newLeaveForm.startDate)} - {formatDate(newLeaveForm.endDate)})
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px] font-bold text-slate-400 tracking-widest ml-1" htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                placeholder="Please provide a reason for your leave request..."
                value={newLeaveForm.reason}
                onChange={(e) => setNewLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-blue-500/10 shadow-sm bg-slate-50 dark:bg-slate-800/50 resize-none font-medium text-sm"
              />
            </div>
          </div>

          <DialogFooter className="px-6 py-4 flex gap-2 border-t border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <Button
              variant="ghost"
              onClick={() => setIsNewLeaveOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFormSubmit}
              disabled={!newLeaveForm.reason.trim() || !newLeaveForm.leave_policy_id || createMutation.isPending}
              className="ml-auto"
            >
              {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={isEventDetailOpen} onOpenChange={setIsEventDetailOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-0 border-none overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-slate-50 dark:border-slate-800">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
              {selectedEvent?.type === 'holiday' ? 'Holiday Details' : 'Leave Request Details'}
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-slate-500">
              {selectedEvent?.type === 'holiday'
                ? 'Public holiday information'
                : 'Employee leave request information'
              }
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <div className="px-6 py-4 space-y-4">
              {selectedEvent.type === 'holiday' ? (
                // Holiday Details
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
                    <div className="h-10 w-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-sm border border-emerald-200">
                      <CalendarIcon className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <span className="font-bold text-lg text-emerald-800 dark:text-emerald-300">{selectedEvent.holiday?.name}</span>
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">{formatDate(selectedEvent.holiday?.holiday_date)}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-3">
                    {selectedEvent.holiday?.location && (
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Location</Label>
                        <p className="font-bold text-slate-800 dark:text-slate-200 text-sm mt-0.5">{selectedEvent.holiday.location}</p>
                      </div>
                    )}
                    {selectedEvent.holiday?.description && (
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Description</Label>
                        <p className="font-medium text-slate-600 dark:text-slate-300 text-sm mt-0.5">{selectedEvent.holiday.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Leave Details
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <Avatar className="h-14 w-14 rounded-2xl shadow-sm border border-slate-100">
                      <AvatarImage src={selectedEvent.employee?.avatar_url} />
                      <AvatarFallback className="bg-[#143A62] text-white font-bold text-lg">
                        {selectedEvent.employee && getInitials(selectedEvent.employee.first_name, selectedEvent.employee.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <div className="text-lg font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
                        {selectedEvent.employee && getFullName(selectedEvent.employee.first_name, selectedEvent.employee.last_name)}
                      </div>
                      <div className="flex items-center space-x-2 mt-0.5">
                        <p className="text-xs text-blue-500 font-bold uppercase tracking-widest">
                          {selectedEvent.employee?.job_title || 'Employee'}
                        </p>
                        {selectedEvent.status && (
                          <div className="scale-[0.85] origin-left -ml-1">
                            {getStatusBadge(selectedEvent.status)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <div className="space-y-1">
                      <Label className="text-[13px] font-bold text-slate-400 tracking-widest">Selected Date</Label>
                      <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                        {formatDate(selectedEvent.date)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[13px] font-bold text-slate-400 tracking-widest">Total Days</Label>
                      <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                        {selectedEvent.leave?.days_count} Days
                      </p>
                    </div>
                    <div className="space-y-1 col-span-2 pt-1 border-t border-slate-200 dark:border-slate-700">
                      <Label className="text-[13px] font-bold text-slate-400 tracking-widest">Full Duration</Label>
                      <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                        {selectedEvent.leave && formatDate(selectedEvent.leave.start_date)} - {selectedEvent.leave && formatDate(selectedEvent.leave.end_date)}
                      </p>
                    </div>
                  </div>

                  {selectedEvent.leave?.reason && (
                    <div className="space-y-1.5">
                      <Label className="text-[13px] font-bold text-slate-400 tracking-widest ml-1">Employee Reason</Label>
                      <div className="p-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-medium italic text-sm shadow-sm">
                        {selectedEvent.leave.reason}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="px-6 py-4 flex gap-2 border-t border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <Button variant="ghost" onClick={() => setIsEventDetailOpen(false)} className="ml-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveCalendar;